/**
 * UTMB Index calculation logic.
 * Port of the Python calculation from utmbindexflation.
 */

import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import RaceYearResultForRunnerView from '@utmb/types/view/RaceYearResultForRunnerView';

// Constants
const NB_SCORES = 5;
const MONTHS_WINDOW = 36;
const MIN_MONTHS_ACTIVITY = 24;
const DEGRADATION_PCTS = [0, 2, 3, 5, 7];
const DEGRADATION_PCTS_SINGLE_RESULT = [0, 3, 3, 5, 7];

// Age degradation matrix (per category, per time bracket)
// Time brackets: 0-6mo, 7-12mo, 13-18mo, 19-24mo, 25-30mo, 31-36+mo
const DEGRADATION_MATRIX: Record<string, number[]> = {
    [PiCategoryEnum.GENERAL]: [-0.4, -0.2, 0.1, 0.5, 1.0, 1.6],
    [PiCategoryEnum.PI20K]: [-0.8, -0.4, 0.4, 0.8, 1.3, 2.2],
    [PiCategoryEnum.PI50K]: [-0.8, -0.4, 0.3, 0.7, 1.2, 2.0],
    [PiCategoryEnum.PI100K]: [-0.8, -0.4, 0.2, 0.6, 1.1, 1.8],
    [PiCategoryEnum.PI100M]: [-0.8, -0.4, 0.1, 0.5, 1.0, 1.6],
};

// --- Native date helpers (replacing date-fns) ---

/** Number of full months between two dates (later - earlier). */
function differenceInMonths(later: Date, earlier: Date): number {
    const monthDiff = (later.getFullYear() - earlier.getFullYear()) * 12 + (later.getMonth() - earlier.getMonth());
    // A full month requires the day-of-month of `later` to be >= the one of `earlier`
    if (later.getDate() < earlier.getDate()) {
        return monthDiff - 1;
    }
    return monthDiff;
}

/** Returns a new date with `months` added. */
function addMonths(date: Date, months: number): Date {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
}

/** Returns a new date at the first day of the month. */
function startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Returns a new date at the start of the day (local midnight), stripping the time. */
function startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// --- Index calculation ---

/**
 * Get the age degradation percentage based on months age and category.
 */
function getAgeDegradation(monthsAge: number, category: PiCategoryEnum): number {
    const matrix = DEGRADATION_MATRIX[category] ?? DEGRADATION_MATRIX[PiCategoryEnum.GENERAL];

    if (monthsAge <= 6) return matrix[0];
    if (monthsAge <= 12) return matrix[1];
    if (monthsAge <= 18) return matrix[2];
    if (monthsAge <= 24) return matrix[3];
    if (monthsAge <= 30) return matrix[4];
    return matrix[5];
}

/**
 * Result enriched with the values computed for the index calculation.
 */
interface ResultWithMeta {
    result: RaceYearResultForRunnerView;
    baseScore: number;
    adjustedScore: number;
    ageDegradationPct: number;
    monthsAge: number;
    category: PiCategoryEnum;
}

/**
 * Filter, enrich and sort the runner results for a given adjustment date and category.
 * The returned list is sorted by adjusted score (descending) and restricted to the
 * 36-month window — i.e. the candidates eligible for the index calculation.
 */
function processResults(results: RaceYearResultForRunnerView[], adjustmentDate: Date, category: PiCategoryEnum): ResultWithMeta[] {
    // A result stays valid as long as the adjustment date is on or before its 36-month
    // anniversary, and expires the day after. Equivalently, its date must fall within
    // [adjustmentDate - 36 months, adjustmentDate]. Comparison is done at day precision so
    // the window edge lands exactly on the anniversary day (e.g. on 25/06/2026 with a
    // 36-month window, 25/06/2023 is still valid but 24/06/2023 is not).
    const adjustmentDay = startOfDay(adjustmentDate);
    const windowStart = startOfDay(addMonths(adjustmentDate, -MONTHS_WINDOW));

    // Filter results with valid dates, positive scores, and within the validity window
    const validResults = results.filter((r) => {
        if (!r.dateIso || r.index <= 0 || r.isDnf) return false;
        const raceDay = startOfDay(new Date(r.dateIso));
        return raceDay.getTime() >= windowStart.getTime() && raceDay.getTime() <= adjustmentDay.getTime();
    });

    // Map to intermediate structure with calculated values
    const processedResults: ResultWithMeta[] = validResults
        .map((r) => {
            const raceDate = new Date(r.dateIso);
            const monthsAge = differenceInMonths(adjustmentDate, raceDate);

            // Use race-specific category for degradation, or "general" for global index
            const degradationCategory = category === PiCategoryEnum.GENERAL ? PiCategoryEnum.GENERAL : r.piCategory;
            const ageDegradationPct = getAgeDegradation(monthsAge, degradationCategory);
            const adjustedScore = r.index * (1 - ageDegradationPct / 100);

            return {
                result: r,
                baseScore: r.index,
                adjustedScore,
                ageDegradationPct,
                monthsAge,
                category: r.piCategory,
            };
        })
        // Filter by category if not "general"
        .filter((r) => category === PiCategoryEnum.GENERAL || r.category === category);

    // Sort by adjusted score descending
    processedResults.sort((a, b) => b.adjustedScore - a.adjustedScore);

    return processedResults;
}

/**
 * Detail of a single retained score in the index calculation.
 */
export interface IndexBreakdownRow {
    /** Underlying race result, or null for a "degraded best score" filler slot. */
    result: RaceYearResultForRunnerView | null;
    /** Raw score obtained on the race (UTMB index of the performance). */
    baseScore: number;
    /** Age of the result in months at the adjustment date. */
    monthsAge: number;
    /** Age degradation percentage applied to the base score (matrix value). */
    ageDegradationPct: number;
    /** Base score after age degradation. */
    adjustedScore: number;
    /** Degradation percentage applied to the best score for this slot. */
    dbsDegradationPct: number;
    /** Degraded best score for this slot. */
    degradedBestScore: number;
    /** Score actually retained for this slot (max of adjusted and degraded best score). */
    retainedScore: number;
    /** Whether the degraded best score was retained instead of the adjusted score. */
    isDbsRetained: boolean;
    /** Whether this slot is a filler (no actual race, only a degraded best score). */
    isFiller: boolean;
}

export interface IndexBreakdown {
    /** Always NB_SCORES rows: the retained results plus degraded-best-score fillers. */
    rows: IndexBreakdownRow[];
    /** Final index (average of the retained scores), or null if no recent activity. */
    index: number | null;
}

/**
 * Build the NB_SCORES breakdown rows from the sorted candidates.
 */
function buildBreakdownRows(processedResults: ResultWithMeta[]): IndexBreakdownRow[] {
    const bestScore = processedResults[0].adjustedScore;

    // Choose degradation percentages
    const degradationPcts = processedResults.length === 1 ? DEGRADATION_PCTS_SINGLE_RESULT : DEGRADATION_PCTS;

    const rows: IndexBreakdownRow[] = [];

    for (let i = 0; i < NB_SCORES; i++) {
        const dbsDegradationPct = degradationPcts[i];
        const degradedBestScore = bestScore * (1 - dbsDegradationPct / 100);

        if (i < processedResults.length) {
            const r = processedResults[i];
            const isDbsRetained = degradedBestScore > r.adjustedScore;
            rows.push({
                result: r.result,
                baseScore: r.baseScore,
                monthsAge: r.monthsAge,
                ageDegradationPct: r.ageDegradationPct,
                adjustedScore: r.adjustedScore,
                dbsDegradationPct,
                degradedBestScore,
                retainedScore: Math.max(r.adjustedScore, degradedBestScore),
                isDbsRetained,
                isFiller: false,
            });
        } else {
            // Filler slot: only the degraded best score counts.
            rows.push({
                result: null,
                baseScore: 0,
                monthsAge: 0,
                ageDegradationPct: 0,
                adjustedScore: 0,
                dbsDegradationPct,
                degradedBestScore,
                retainedScore: degradedBestScore,
                isDbsRetained: true,
                isFiller: true,
            });
        }
    }

    return rows;
}

/**
 * Calculate the UTMB index breakdown for a runner at a given adjustment date.
 * Exposes the per-result detail (adjusted score, degraded best score, retained score)
 * used to build the index, on top of the final index value.
 */
export function calculateIndexBreakdown(
    results: RaceYearResultForRunnerView[],
    category: PiCategoryEnum = PiCategoryEnum.GENERAL,
    adjustmentDate: Date = new Date()
): IndexBreakdown {
    const processedResults = processResults(results, adjustmentDate, category);

    // Check if there are any valid results
    if (processedResults.length === 0) {
        return { rows: [], index: null };
    }

    const rows = buildBreakdownRows(processedResults);

    // Check for recent activity (latest result must be within MIN_MONTHS_ACTIVITY)
    const minMonthsAge = Math.min(...processedResults.map((r) => r.monthsAge));
    if (minMonthsAge > MIN_MONTHS_ACTIVITY) {
        return { rows, index: null };
    }

    // Calculate final index (average of the retained scores). Floored so the value plotted
    // on the evolution chart never overstates the index.
    const index = Math.floor(rows.reduce((sum, r) => sum + r.retainedScore, 0) / rows.length);

    return { rows, index };
}

/**
 * Calculate the UTMB index for a runner at a given adjustment date.
 */
function calculateIndex(results: RaceYearResultForRunnerView[], adjustmentDate: Date, category: PiCategoryEnum): number | null {
    return calculateIndexBreakdown(results, category, adjustmentDate).index;
}

export interface IndexEvolutionPoint {
    date: number; // Unix timestamp (ms)
    index: number | null;
}

/**
 * Generate time points for index evolution curve.
 * Points are generated at:
 * - First of each month
 * - Each race result date
 * - 36 months after each race result (when it drops out of the window)
 */
function generateTimePoints(results: RaceYearResultForRunnerView[]): Date[] {
    const validResults = results.filter((r) => r.dateIso && r.index > 0 && !r.isDnf);

    if (validResults.length === 0) return [];

    // Get race dates
    const raceDates = validResults.map((r) => new Date(r.dateIso));
    const minRaceDate = new Date(Math.min(...raceDates.map((d) => d.getTime())));
    const maxRaceDate = new Date(Math.max(...raceDates.map((d) => d.getTime())));

    // Extend to 26 months after the last race (enough to show index becoming null at 24mo)
    const endDate = addMonths(maxRaceDate, 26);

    // Use a Set to deduplicate timestamps
    const timestamps = new Set<number>();

    // Add monthly points (first of each month)
    let current = startOfMonth(minRaceDate);
    while (current <= endDate) {
        timestamps.add(current.getTime());
        current = addMonths(current, 1);
    }

    // Add race dates
    for (const raceDate of raceDates) {
        timestamps.add(raceDate.getTime());
    }

    // Add the day after each result's 36-month anniversary (when it drops out of the window)
    for (const raceDate of raceDates) {
        const dropDate = startOfDay(addMonths(raceDate, MONTHS_WINDOW));
        dropDate.setDate(dropDate.getDate() + 1);
        if (dropDate <= endDate) {
            timestamps.add(dropDate.getTime());
        }
    }

    // Sort and convert back to dates
    return Array.from(timestamps)
        .sort((a, b) => a - b)
        .map((ts) => new Date(ts));
}

/**
 * Calculate index evolution over time for a runner.
 * Returns an array of points with the computed index at each point in time.
 */
export function calculateIndexEvolution(results: RaceYearResultForRunnerView[], category: PiCategoryEnum = PiCategoryEnum.GENERAL): IndexEvolutionPoint[] {
    const timePoints = generateTimePoints(results);

    if (timePoints.length === 0) return [];

    return timePoints.map((date) => ({
        date: date.getTime(),
        index: calculateIndex(results, date, category),
    }));
}

export interface IndexesEvolutionPoint {
    date: number; // Unix timestamp (ms)
    indexes: (number | null)[]; // One per runner, in the given order
}

/**
 * Calculate the index evolution of several runners on shared time points, to plot them together.
 * The time points of every runner are merged, so each curve matches its calculateIndexEvolution one.
 */
export function calculateIndexesEvolution(
    runnersResults: RaceYearResultForRunnerView[][],
    category: PiCategoryEnum = PiCategoryEnum.GENERAL
): IndexesEvolutionPoint[] {
    const timestamps = new Set(runnersResults.flatMap((results) => generateTimePoints(results).map((date) => date.getTime())));

    return Array.from(timestamps)
        .sort((a, b) => a - b)
        .map((timestamp) => {
            const date = new Date(timestamp);

            return {
                date: timestamp,
                indexes: runnersResults.map((results) => calculateIndex(results, date, category)),
            };
        });
}
